import { useState, useEffect } from 'react';

/**
 * usePrefersReducedMotion - Hook to detect if user prefers reduced motion
 *
 * Detects the `prefers-reduced-motion` media query and returns a boolean.
 * Components should use this to disable/simplify animations for accessibility.
 *
 * @returns {boolean} - True if user prefers reduced motion
 *
 * @example
 * ```tsx
 * const prefersReducedMotion = usePrefersReducedMotion();
 *
 * return (
 *   <div className={prefersReducedMotion ? '' : 'animate-bounce'}>
 *     Content
 *   </div>
 * );
 * ```
 *
 * Issue #499: WCAG 2.1 AA compliance for motion sensitivity
 */
export function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if matchMedia is supported
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Listen for changes
    const handler = (event: MediaQueryListEvent | MediaQueryList) => {
      setPrefersReducedMotion(event.matches);
    };

    // Use addEventListener if supported (modern browsers)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      // Fallback for older browsers
       
      mediaQuery.addListener(handler);
       
      return () => mediaQuery.removeListener(handler);
    }
  }, []);

  return prefersReducedMotion;
}

export default usePrefersReducedMotion;
